import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/* Soft colour blob for background depth */
export const Blob = ({ color = "rgba(194,109,82,0.18)", className = "", size = 520 }) => (
  <div
    aria-hidden
    className={`pointer-events-none absolute rounded-full blur-3xl ${className}`}
    style={{ width: size, height: size, background: `radial-gradient(circle, ${color}, transparent 70%)` }}
  />
);

/* Mouse-tracked 3D tilt wrapper */
export const Tilt = ({ children, className = "", max = 7, testId }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * max * 2);
    rx.set(-py * max * 2);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className={className} style={{ perspective: 1200 }} data-testid={testId}>
      <motion.div className="tilt-card h-full" style={{ rotateX: srx, rotateY: sry }}>
        {children}
      </motion.div>
    </div>
  );
};

/* Small glass "interface fragment" */
export const Fragment = ({ children, className = "", testId }) => (
  <div data-testid={testId} className={`glass rounded-2xl px-4 py-3.5 ${className}`}>
    {children}
  </div>
);

/* Abstract KPI bars (no numbers — purely decorative) */
export const MiniBars = ({ color = "#C26D52", heights = [40, 62, 48, 78, 66, 92], className = "" }) => (
  <div className={`flex items-end gap-1.5 h-12 ${className}`}>
    {heights.map((h, i) => (
      <span
        key={i}
        className="bar-grow w-2.5 rounded-t-sm"
        style={{ height: `${h}%`, background: `linear-gradient(180deg, ${color}, ${color}88)`, animationDelay: `${i * 90}ms` }}
      />
    ))}
  </div>
);

/* Abstract sparkline */
export const Sparkline = ({ color = "#2C4A6F", className = "" }) => (
  <svg viewBox="0 0 120 40" className={`w-full h-10 ${className}`} fill="none">
    <path d="M2 32 C 18 30, 22 18, 34 20 S 50 30, 62 22 S 80 8, 96 12 S 110 4, 118 6" stroke={color} strokeWidth="2" strokeLinecap="round" className="draw-line" />
    <path d="M2 32 C 18 30, 22 18, 34 20 S 50 30, 62 22 S 80 8, 96 12 S 110 4, 118 6 V 40 H 2 Z" fill={color} opacity="0.08" />
  </svg>
);

/* Connected nodes diagram */
export const NodeLine = ({ color = "#0E1B31", className = "" }) => (
  <svg viewBox="0 0 320 80" className={`w-full ${className}`} fill="none">
    <path d="M10 40 H 310" stroke={color} strokeOpacity="0.15" strokeWidth="1" />
    <path d="M10 40 C 60 40, 70 14, 110 14 S 160 66, 210 66 S 270 40, 310 40" stroke={color} strokeWidth="1.5" className="draw-line" />
    {[10, 110, 210, 310].map((x, i) => (
      <circle key={i} cx={x} cy={i === 1 ? 14 : i === 2 ? 66 : 40} r="4" fill={color} />
    ))}
  </svg>
);

export const Orbit = ({ className = "", color = "#0E1B31" }) => (
  <svg viewBox="0 0 200 200" className={`spin-slow ${className}`} fill="none">
    <circle cx="100" cy="100" r="96" stroke={color} strokeOpacity="0.12" strokeDasharray="2 6" />
    <circle cx="100" cy="100" r="70" stroke={color} strokeOpacity="0.18" />
    <circle cx="100" cy="4" r="4" fill={color} />
    <circle cx="170" cy="100" r="3" fill={color} opacity="0.6" />
  </svg>
);

export const useParallax = (scrollYProgress, from = 0, to = 60) => useTransform(scrollYProgress, [0, 1], [from, to]);
